import {FunctionBknBstGroupedProperties} from 'shared/dtos/function-bkn-bst-grouped';
import {Mitarbeiter} from 'shared/dtos/mitarbeiter';
import {RecordSummary} from 'shared/dtos/record-summary';

import {TemostaDataListValidationDto} from 'shared/dtos/temosta-data-list-validation-dto';
import {AdditionalInfo} from 'shared/dtos/wizard/additional-info';
import {MitarbeiterProperties} from 'shared/dtos/x-mitarbeiterBase';
import {
  TesmostaDatalistValidatorValue,
  VALIDATOR_ERROR_NAMES
} from 'shared/EnumsConstants/tesmosta-datalist-validator-value.enum';
import {GlobalConfig} from 'shared/global-config';

const MAX_DECIMALS = 5;
const DISALLOWED_BKB_BST_COMBO = '(1,6), (1,7), (1,8), (2,6), (2,7), (2,8)';
const DISALLOWED_AUSBILDUNG_BKN_COMBO =
  '(1,7), (1,8), (2,7), (2,8), (3,7), (3,8)';

export function calculateDataSummary(
  mitarbeiters: Mitarbeiter[]
): RecordSummary {
  const totalMitarbeiter = mitarbeiters.length;
  const amountSuspicous = mitarbeiters
    .map(row => row.validator)
    .filter(val =>
      val.some(
        c => c && c.value === TesmostaDatalistValidatorValue.AuffaelligerWert
      )
    ).length;
  const ungultig = mitarbeiters.filter(
    m => m.summaryStatus === TesmostaDatalistValidatorValue.UngueltigerWert
  ).length;
  const abgeschlossen = mitarbeiters.filter(
    m =>
      m.summaryStatus ===
      TesmostaDatalistValidatorValue.AuswertungAusgeschlossen
  ).length;

  const gultig =
    ungultig + abgeschlossen === 0
      ? totalMitarbeiter
      : totalMitarbeiter - (ungultig + abgeschlossen);

  return {
    total: totalMitarbeiter,
    ungueltig: ungultig,
    ausgeschlossen: abgeschlossen,
    gueltig: gultig,
    auffaellig: amountSuspicous
  };
}

export async function validateSingleMitarbeiter(
  additionalInfo: AdditionalInfo,
  mitarbeiter: Mitarbeiter,
  mitarbeiters: Mitarbeiter[],
  isForM2 = false
) {
  const validator = new Array<TemostaDataListValidationDto>();

  const validatorAlter = alterRegardingReferenzMonatValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.alter
  );
  validator[MitarbeiterProperties.alter] = validatorAlter;

  const validatorGeschlecht = geschlechtValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.geschlecht
  );
  validator[MitarbeiterProperties.geschlecht] = validatorGeschlecht;

  const validatorDienstjahre = dienstjahreValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.dienstjahre
  );
  validator[MitarbeiterProperties.dienstjahre] = validatorDienstjahre;

  const validatorAusbildung = ausbildungValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.ausbildung
  );
  validator[MitarbeiterProperties.ausbildung] = validatorAusbildung;

  const validatorAnMonatslohnBeschaeftigungsgrad = beschaeftigungsgradValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.anMonatslohnBeschaeftigungsgrad
  );
  validator[MitarbeiterProperties.anMonatslohnBeschaeftigungsgrad] =
    validatorAnMonatslohnBeschaeftigungsgrad;

  const validatorBezahlteStundenValidator = bezahlteStundenValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
  );
  validator[
    MitarbeiterProperties.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
  ] = validatorBezahlteStundenValidator;

  const validatorGrundLohn = grundlohnValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.grundlohn
  );
  validator[MitarbeiterProperties.grundlohn] = validatorGrundLohn;

  const validatorZulagen = zulagenValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.zulagen
  );
  validator[MitarbeiterProperties.zulagen] = validatorZulagen;

  const validatorSonderzahlungen = sonderzahlungenValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.sonderzahlungen
  );
  validator[MitarbeiterProperties.sonderzahlungen] = validatorSonderzahlungen;

  const validatorBetrieblicheWoechentlicheArbeitszeit = bUWAZValidator(
    additionalInfo.betriebsueblicheArbeitszeitImReferenzMonat,
    mitarbeiter.grundgesamtheit,
    mitarbeiter.betrieblicheWoechentlicheArbeitszeit
  );
  validator[MitarbeiterProperties.betrieblicheWoechentlicheArbeitszeit] =
    validatorBetrieblicheWoechentlicheArbeitszeit;

  const validatorGrundgesamtheit = grundgesamtheitValidator(mitarbeiter);
  validator[MitarbeiterProperties.grundgesamtheit] = validatorGrundgesamtheit;

  // Validate beschäftigungsgrad and BezahlteStundenRule
  beschaeftigungsgradAndBezahlteStundenRule(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.anMonatslohnBeschaeftigungsgrad,
    mitarbeiter.anStundenlohnAnzahlBezahlteStundenImReferenzmonat,
    validator
  );

  if (
    !setValidatorForGrundLohnIsNegativ(
      validator,
      // eslint-disable-next-line
      Number(mitarbeiter.grundlohn)
    )
  ) {
    if (
      !setValidatorForGrundLohnNull(
        validator,
        // eslint-disable-next-line
        Number(mitarbeiter.zulagen),
        // eslint-disable-next-line
        Number(mitarbeiter.dreizehnterMonatslohn),
        // eslint-disable-next-line
        Number(mitarbeiter.sonderzahlungen),
        // eslint-disable-next-line
        Number(mitarbeiter.grundlohn),
        mitarbeiter.funktion
      )
    ) {
      if (isForM2) {
        validator[MitarbeiterProperties.funktion] = funktionValidator(
          mitarbeiter.grundgesamtheit,
          mitarbeiter.funktion
        );
      } else {
        funktionBknAndBstRowAreValidationChecker(validator, mitarbeiter);
      }

      const validatorDreizehnterMonatslohn =
        dreizehnterMonatslohnValidator(mitarbeiter);
      validator[MitarbeiterProperties.dreizehnterMonatslohn] =
        validatorDreizehnterMonatslohn;

      setValidatorForGrundLohnNiedrigerAlsBezahltenStunden(
        validator,
        // eslint-disable-next-line
        Number(mitarbeiter.anStundenlohnAnzahlBezahlteStundenImReferenzmonat),
        // eslint-disable-next-line
        Number(mitarbeiter.grundlohn)
      );

      setValidatorForAlterAndDienstjahre(mitarbeiter, validator);
    }
  }

  validator[MitarbeiterProperties.laufnummer] = validateLaufnummern(
    mitarbeiter,
    mitarbeiters
  );
  mitarbeiter.validator = validator;
  const validatores = validator.filter(elt => elt);

  if (validatores.length > 0) {
    if (
      validatores.filter(
        elt =>
          elt.value === TesmostaDatalistValidatorValue.AuswertungAusgeschlossen
      ).length > 0
    ) {
      mitarbeiter.summaryStatus =
        TesmostaDatalistValidatorValue.AuswertungAusgeschlossen;
    } else if (
      validatores.filter(
        elt => elt.value === TesmostaDatalistValidatorValue.UngueltigerWert
      ).length > 0
    ) {
      mitarbeiter.summaryStatus =
        TesmostaDatalistValidatorValue.UngueltigerWert;
    } else {
      mitarbeiter.summaryStatus = TesmostaDatalistValidatorValue.KorrektWert;
    }
  } else {
    mitarbeiter.summaryStatus = TesmostaDatalistValidatorValue.KorrektWert;
  }
}

export function validateLaufnummern(
  mitarbeiter: Mitarbeiter,
  mitarbeiters: Mitarbeiter[]
) {
  if (
    mitarbeiters
      .filter(ma => isAllowValidation(ma.grundgesamtheit))
      .filter(ma => ma.laufnummer === mitarbeiter.laufnummer).length > 1
  ) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.UNIQUE_LAUFNUMMER,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }
  return laufnummerValidator(
    mitarbeiter.grundgesamtheit,
    mitarbeiter.laufnummer
  );
}

export function funktionBknAndBstRowAreValidationChecker(
  validator: TemostaDataListValidationDto[],
  mitarbeiter: Mitarbeiter
): boolean {
  if (!isAllowValidation(mitarbeiter.grundgesamtheit)) {
    return null;
  }

  // function validator
  const funktion = functionValidation(mitarbeiter, validator);

  // betriebilichesKompetenzniveau validator
  const bkn = betriebilichesKompetenzniveauValidation(mitarbeiter, validator);

  // beruflicheStellung validator
  const bst = betweenValidator(mitarbeiter.beruflicheStellung, 1, 5, true);
  validator[MitarbeiterProperties.beruflicheStellung] = bst;

  return !funktion && !bkn && !bst;
}

export function ausbildungFunktionAndBknRowValidationChecker(
  validator: TemostaDataListValidationDto[],
  mitarbeiter: Mitarbeiter
): boolean {
  if (!isAllowValidation(mitarbeiter.grundgesamtheit)) {
    return null;
  }

  // function validator
  const funktion = functionValidation(mitarbeiter, validator);

  // betriebilichesKompetenzniveau validator
  const bkn = betriebilichesKompetenzniveauValidation(mitarbeiter, validator);

  // ausbildung validator
  const ausdildung = betweenValidator(mitarbeiter.ausbildung, 1, 8, true);
  validator[MitarbeiterProperties.ausbildung] = ausdildung;

  return !funktion && !bkn && !ausdildung;
}

function betriebilichesKompetenzniveauValidation(
  mitarbeiter: Mitarbeiter,
  validator: TemostaDataListValidationDto[]
) {
  const bkn = integerBetweenValidator(
    mitarbeiter.betrieblichesKompetenzniveau,
    1,
    GlobalConfig.anfMax,
    true
  );
  validator[MitarbeiterProperties.betrieblichesKompetenzniveau] = bkn;
  return bkn;
}

function functionValidation(
  mitarbeiter: Mitarbeiter,
  validator: TemostaDataListValidationDto[]
) {
  const funktionName = mitarbeiter.funktion?.toString().trim();
  const isInvalid = !funktionName;

  const funktion = isInvalid
    ? getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
        TesmostaDatalistValidatorValue.UngueltigerWert,
        true
      )
    : null;

  validator[MitarbeiterProperties.funktion] = funktion;
  return funktion;
}

export function populatefunctionBknAndBstValidator(
  mitarbeiter: Mitarbeiter[]
): void {
  const lsfOfFunctionWithDiffCodification =
    getItemsWithInconsistentCodification(mitarbeiter);

  // validate values and check pair bkn / bst
  mitarbeiter.forEach(ma => processStandardValidation(ma));

  // Process items with different codification
  lsfOfFunctionWithDiffCodification.forEach(ma =>
    processDifferentCodification(ma)
  );
}

function processStandardValidation(ma: Mitarbeiter): void {
  functionValidation(ma, ma.validator);

  if (!shouldProcessValidation(ma)) {
    return;
  }

  if (funktionBknAndBstRowAreValidationChecker(ma.validator, ma)) {
    const combineKey = `(${ma.beruflicheStellung},${ma.betrieblichesKompetenzniveau})`;
    notAllowedCombineCodificationValidator(combineKey, ma.validator, false);
  }
}

function processDifferentCodification(ma: Mitarbeiter): void {
  if (!shouldProcessValidation(ma)) {
    return;
  }

  if (funktionBknAndBstRowAreValidationChecker(ma.validator, ma)) {
    const lstOfMitarbeiterProp = [
      MitarbeiterProperties.betrieblichesKompetenzniveau,
      MitarbeiterProperties.funktion,
      MitarbeiterProperties.beruflicheStellung
    ];
    addValidatorAccordingToProperty(
      lstOfMitarbeiterProp,
      ma,
      VALIDATOR_ERROR_NAMES.UNIQUE_CODIFICATION
    );
  }
}

function shouldProcessValidation(ma: Mitarbeiter): boolean {
  return (
    isAllowValidation(ma.grundgesamtheit) &&
    !isGrundLohnZeroAndZulagenAnd13MontaAndSonderzahlungenZero(
      ma.grundlohn,
      ma.zulagen,
      ma.dreizehnterMonatslohn,
      ma.sonderzahlungen
    )
  );
}

export function populateAusbildungFunctionAndBknValidator(
  mitarbeiters: Mitarbeiter[]
): void {
  const lsfOfFunctionWithDiffCodification =
    getAusbildungBksItemsWithInconsistentCodification(mitarbeiters);

  // validate values and check pair ausbildung / bkn
  for (const elt of mitarbeiters) {
    if (isAllowValidation(elt.grundgesamtheit)) {
      // validate values and check pair ausbildung / bkn
      const combineKey = `(${elt.betrieblichesKompetenzniveau},${elt.ausbildung})`;
      notAllowedCombineAusbildungBknValidator(combineKey, elt.validator);
    }
  }
}

export function populateAlterAndDienstjahreValidator(
  mitarbeiters: Mitarbeiter[]
) {
  for (const item of mitarbeiters) {
    if (isAllowValidation(item.grundgesamtheit)) {
      if (item.alter || item.dienstjahre) {
        setValidatorForAlterAndDienstjahre(item, item.validator);
      }
    }
  }
}

export function populateBstAnfValidator(mitarbeiters: Mitarbeiter[]) {
  for (const item of mitarbeiters) {
    if (isAllowValidation(item.grundgesamtheit)) {
      if (item.alter || item.dienstjahre) {
        setValidatorForBstAnf(item, item.validator);
      }
    }
  }
}

function setValidatorForAlterAndDienstjahre(
  mitarbeiter: Mitarbeiter,
  validator: TemostaDataListValidationDto[]
) {
  if (mitarbeiter.grundgesamtheit > 1) {
    return;
  }

  const rangeValue = new RangeValues(12, 16);
  const effectiveAlter =
    Number(mitarbeiter.alter) - Number(mitarbeiter.dienstjahre);

  // orange alter and dienstjahre
  if (effectiveAlter > rangeValue.min1 && effectiveAlter < rangeValue.max1) {
    validator[MitarbeiterProperties.alter] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.ALTER_DIENST_JAHRE_COMBINATION_NOT_ALLOWED,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
    validator[MitarbeiterProperties.dienstjahre] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.ALTER_DIENST_JAHRE_COMBINATION_NOT_ALLOWED,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
  }

  // rot alter and dienstjahre
  if (effectiveAlter <= rangeValue.min1) {
    validator[MitarbeiterProperties.alter] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.ALTER_DIENST_JAHRE_COMBINATION_NOT_ALLOWED,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
    validator[MitarbeiterProperties.dienstjahre] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.ALTER_DIENST_JAHRE_COMBINATION_NOT_ALLOWED,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }
}

export function setValidatorForBstAnf(
  mitarbeiter: Mitarbeiter,
  validator: TemostaDataListValidationDto[]
) {
  const idxBkn = MitarbeiterProperties.betrieblichesKompetenzniveau; // anf (bkn)
  const idxBst = MitarbeiterProperties.beruflicheStellung; // bst
  const idxFunktion = MitarbeiterProperties.funktion; // funktion

  if (validator) {
    const combineKey = `(${mitarbeiter.beruflicheStellung},${mitarbeiter.betrieblichesKompetenzniveau})`;
    if (DISALLOWED_BKB_BST_COMBO.includes(combineKey)) {
      validator[idxBkn] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.BST_ANF_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
      validator[idxBst] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.BST_ANF_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
      validator[idxFunktion] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.BST_ANF_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }
  }
}

export function notAllowedCombineCodificationValidator(
  combineKey: string,
  validator: TemostaDataListValidationDto[],
  isForFuntionCoding: boolean
) {
  const idxFunktion = isForFuntionCoding
    ? FunctionBknBstGroupedProperties.funktion
    : MitarbeiterProperties.funktion;
  const idxBkn = isForFuntionCoding
    ? FunctionBknBstGroupedProperties.bkn
    : MitarbeiterProperties.betrieblichesKompetenzniveau;
  const idxBst = isForFuntionCoding
    ? FunctionBknBstGroupedProperties.bst
    : MitarbeiterProperties.beruflicheStellung;

  if (validator) {
    if (DISALLOWED_BKB_BST_COMBO.includes(combineKey)) {
      if (!isForFuntionCoding) {
        validator[idxBkn] = getTesmostaDataValidator(
          VALIDATOR_ERROR_NAMES.UNIQUE_CODIFICATION,
          TesmostaDatalistValidatorValue.AuffaelligerWert,
          false
        );
        validator[idxBst] = getTesmostaDataValidator(
          VALIDATOR_ERROR_NAMES.UNIQUE_CODIFICATION,
          TesmostaDatalistValidatorValue.AuffaelligerWert,
          false
        );
      }
      validator[idxFunktion] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.NOT_ALLOWED_COMBINATION,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }
  }
}

function addValidatorAccordingToProperty(
  lstOfMitarbeiterProp: MitarbeiterProperties[],
  item: Mitarbeiter,
  validationName: string
) {
  lstOfMitarbeiterProp.forEach(prop => {
    item.validator[prop] = getTesmostaDataValidator(
      validationName,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
  });
}

function alterRegardingReferenzMonatValidator(
  grundgesamtheit: number,
  controlValue: string | number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }

  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }

  if (!isNaN(Number(controlValue)) && !Number.isInteger(Number(controlValue))) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.TYPE_MISMATCH,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }

  const rangeValue = new RangeValues(13, 15, 71, 100);

  return betweenWithBothTermeWithEqualsValidator(
    controlValue,
    13,
    100,
    true,
    rangeValue
  );
}

export function notAllowedCombineAusbildungBknValidator(
  combineKey: string,
  validator: TemostaDataListValidationDto[]
) {
  const idxFunktion = MitarbeiterProperties.funktion;
  const idxBkn = MitarbeiterProperties.betrieblichesKompetenzniveau;
  const idxAusbilldung = MitarbeiterProperties.ausbildung;

  if (validator) {
    if (DISALLOWED_AUSBILDUNG_BKN_COMBO.includes(combineKey)) {
      validator[idxBkn] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUSBILDUNG_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
      validator[idxAusbilldung] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUSBILDUNG_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );

      validator[idxFunktion] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUSBILDUNG_COMBINATION_NOT_ALLOWED,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    } // else {
    //   validator[idxBkn] = null;
    //   validator[idxAusbilldung] = null;
    //   validator[idxFunktion] = null;
    // }
  }
}

function geschlechtValidator(
  grundgesamtheit: number,
  controlValue: string
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }

  if (!controlValue || controlValue === '') {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }

  const str = '|F|M|';
  if (!str.includes(`|${controlValue}|`)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.MUST_BE_M_OR_F,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }
  return null;
}

function funktionValidator(
  grundgesamtheit: number,
  controlValue?: string
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit) || !isRequiredValue(controlValue)) {
    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    true
  );
}

function dienstjahreValidator(
  grundgesamtheit: number,
  controlValue: string | number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  if (countDecimals(+controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }

  const rangeValue = new RangeValues(55, 85);
  const minDienstjahre = 0;
  return betweenWithOnlyOnePartAndContainsEqualOnLastTermeValidator(
    controlValue,
    minDienstjahre,
    85,
    true,
    rangeValue
  );
}

function grundgesamtheitValidator(
  mitarbeiter: Mitarbeiter
): TemostaDataListValidationDto {
  // eslint-disable-next-line no-negated-condition
  const grundgesamtheit = !mitarbeiter.grundgesamtheit
    ? 0
    : mitarbeiter.grundgesamtheit;
  const validator = betweenValidator(grundgesamtheit.toString(), 1, 5, true);
  if (validator) {
    return validator;
  }
  if (mitarbeiter.grundgesamtheit > 1) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.GENERIC_ERROR,
      TesmostaDatalistValidatorValue.AuswertungAusgeschlossen,
      false
    );
  }
  return null;
}

function laufnummerValidator(
  grundgesamtheit: number,
  controlValue: string
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  return alphanumerischValidator(controlValue, true, true);
}

function ausbildungValidator(
  grundgesamtheit: number,
  controlValue: string | number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  if (!isNaN(+controlValue) && !Number.isInteger(+controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.TYPE_MISMATCH,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  }
  return betweenValidator(+controlValue, 1, 8, true);
}

function beschaeftigungsgradValidator(
  grundgesamtheit: number,
  controlValue: number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  // eslint-disable-next-line
  if (countDecimals(+controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }
  // eslint-disable-next-line
  return betweenValidator(+controlValue, 0, 150, false);
}

function bezahlteStundenValidator(
  grundgesamtheit: number,
  controlValue: number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  // eslint-disable-next-line
  if (countDecimals(+controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }
  // eslint-disable-next-line
  return betweenValidator(+controlValue, 0, 300, false);
}

function grundlohnValidator(
  grundgesamtheit: number,
  controlValue?: number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // eslint-disable-next-line
  if (countDecimals(+controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }

  // eslint-disable-next-line
  return minValidator(true, +controlValue, false);
}

function zulagenValidator(
  grundgesamtheit: number,
  controlValue?: number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  if (countDecimals(controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }
  // eslint-disable-next-line
  return minValidator(false, +controlValue, true);
}

function dreizehnterMonatslohnValidator(
  mitarbeiter: Mitarbeiter
): TemostaDataListValidationDto {
  if (!isAllowValidation(mitarbeiter.grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  if (
    countDecimals(mitarbeiter.dreizehnterMonatslohn) > MAX_DECIMALS ||
    isNaN(mitarbeiter.dreizehnterMonatslohn)
  ) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }

  // eslint-disable-next-line
  if (+mitarbeiter.dreizehnterMonatslohn) {
    // eslint-disable-next-line
    if (+mitarbeiter.dreizehnterMonatslohn < 0) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
        TesmostaDatalistValidatorValue.UngueltigerWert,
        false
      );
    }
    const dreizehnMl = mitarbeiter.dreizehnterMonatslohn
      ? // eslint-disable-next-line
        +mitarbeiter.dreizehnterMonatslohn
      : 0;
    const val = mitarbeiter.grundlohn ? mitarbeiter.grundlohn * 0.25 : 0;
    if (dreizehnMl > val) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.RANGE_ERROR,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }

    const valAnteilMin = mitarbeiter.grundlohn * 0.08;
    // eslint-disable-next-line
    if (+mitarbeiter.dreizehnterMonatslohn < valAnteilMin) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUFFAELLIGE_RANGE_ERROR,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }
  }
  return null;
}

function sonderzahlungenValidator(
  grundgesamtheit: number,
  controlValue?: number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  if (countDecimals(controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }

  // eslint-disable-next-line
  return minValidator(false, +controlValue, true);
}

function bUWAZValidator(
  additionalInfoBuWaz: string | number,
  grundgesamtheit: number,
  controlValue: string | number
): TemostaDataListValidationDto {
  if (!isAllowValidation(grundgesamtheit)) {
    return null;
  }
  // check for 5 decimals
  if (countDecimals(+controlValue) > MAX_DECIMALS) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.FORMAT_ERROR,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      false
    );
  }

  const rangeValue = new RangeValues(50, 100);
  const validation = betweenWithOnlyOnePartAndContainsEqualOnLastTermeValidator(
    controlValue,
    1,
    100,
    true,
    rangeValue
  );

  if (validation) {
    return validation;
  }
  return buwazPercentageValidation(Number(additionalInfoBuWaz), controlValue);
}

function buwazPercentageValidation(
  additionalInfoBuwz: number,
  betrieblicheWoechentlicheArbeitszeit: string | number
) {
  if (additionalInfoBuwz && betrieblicheWoechentlicheArbeitszeit) {
    const perc = Number(
      (
        (+betrieblicheWoechentlicheArbeitszeit * 100) /
        additionalInfoBuwz
      ).toFixed(2)
    );

    if (perc < 30) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.VALUE_BUWAZ_IS_LESS_THAN_30,
        TesmostaDatalistValidatorValue.UngueltigerWert,
        false
      );
    }

    if (perc <= 89.99) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.VALUE_BUWAZ_IS_LESS_THAN_90,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }
    return null;
  }
  return null;
}

function setValidatorForGrundLohnIsNegativ(
  validator: TemostaDataListValidationDto[],
  grundLohn: number | null | undefined
): boolean {
  if (grundLohn !== null && grundLohn < 0) {
    validator[MitarbeiterProperties.grundlohn] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
    return true;
  }
  return false;
}

function setValidatorForGrundLohnNull(
  validator: TemostaDataListValidationDto[],
  zulagen: number | null | undefined,
  dreizehnterMonatslohn: number | null | undefined,
  sonderzahlungen: number | null | undefined,
  grundLohn: number | null | undefined,
  funktion: string
): boolean {
  if (
    isGrundLohnZeroAndZulagenAnd13MontaAndSonderzahlungenZero(
      grundLohn,
      zulagen,
      dreizehnterMonatslohn,
      sonderzahlungen
    )
  ) {
    validator[MitarbeiterProperties.grundlohn] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.GRUND_LOHN_ZERO_ZULAGEN_13MONAT_SONDERZAHLUNGEN_ZERO,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
    return true;
  } else if (
    (grundLohn === null || grundLohn === 0) &&
    (zulagen > 0 || dreizehnterMonatslohn > 0 || sonderzahlungen > 0)
  ) {
    if (funktion !== '' && funktion !== null && funktion !== undefined) {
      validator[MitarbeiterProperties.grundlohn] = getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.GRUND_LOHN_ZERO_ZULAGEN_13MONAT_SONDERZAHLUNGEN_NOTZERO,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        true
      );
      return true;
    }
  }
  return false;
}

function isGrundLohnZeroAndZulagenAnd13MontaAndSonderzahlungenZero(
  grundLohn: any,
  zulagen: any,
  dreizehnterMonatslohn: any,
  sonderzahlungen: any
): boolean {
  const toNum = (v: any) => Number(v) || 0;

  return (
    toNum(grundLohn) === 0 &&
    toNum(zulagen) === 0 &&
    toNum(dreizehnterMonatslohn) === 0 &&
    toNum(sonderzahlungen) === 0
  );
}

function setValidatorForGrundLohnNiedrigerAlsBezahltenStunden(
  validator: TemostaDataListValidationDto[],
  bezahltenStunden: number,
  grundLohn: number
) {
  if (grundLohn < bezahltenStunden) {
    validator[MitarbeiterProperties.grundlohn] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.GRUND_LOHN_LESS_THAN_BEZAHLT_STUNDEN,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
    validator[
      MitarbeiterProperties.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
    ] = getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.GRUND_LOHN_LESS_THAN_BEZAHLT_STUNDEN,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
  }
}

function beschaeftigungsgradAndBezahlteStundenValidator(
  mitarbeiters: Mitarbeiter[]
) {
  for (const mitarbeiter of mitarbeiters) {
    beschaeftigungsgradAndBezahlteStundenRule(
      mitarbeiter.grundgesamtheit,
      mitarbeiter.anMonatslohnBeschaeftigungsgrad,
      mitarbeiter.anStundenlohnAnzahlBezahlteStundenImReferenzmonat,
      mitarbeiter.validator
    );
  }
}

function beschaeftigungsgradAndBezahlteStundenRule(
  grundgesamtheit: string | number,
  beschaeftigungsgrad: string | number,
  bezahlteStundenImReferenzmonat: string | number,
  validator: TemostaDataListValidationDto[]
) {
  if (isAllowValidation(grundgesamtheit)) {
    if (
      hasValue(bezahlteStundenImReferenzmonat) &&
      hasValue(beschaeftigungsgrad)
    ) {
      if (
        shouldSetValidator(bezahlteStundenImReferenzmonat, beschaeftigungsgrad)
      ) {
        setValidatorForBeschaetigunsgradAndBezahlStunden(validator);
      }
    } else if (hasValue(beschaeftigungsgrad)) {
      if (+beschaeftigungsgrad < 0 || +beschaeftigungsgrad === 0) {
        setValidatorForBeschaetigunsgradAndBezahlStunden(validator);
      }
    } else if (
      +bezahlteStundenImReferenzmonat < 0 ||
      +bezahlteStundenImReferenzmonat === 0
    ) {
      setValidatorForBeschaetigunsgradAndBezahlStunden(validator);
    }
    setBeschaeftigungsgradRangeValidator(
      validator,
      +beschaeftigungsgrad,
      +bezahlteStundenImReferenzmonat
    );
  }
}

function setBeschaeftigungsgradRangeValidator(
  validator: TemostaDataListValidationDto[],
  beschaeftigungsgrad: number,
  bezahlteStundenImReferenzmonat: number
) {
  if (
    validator[MitarbeiterProperties.anMonatslohnBeschaeftigungsgrad] === null &&
    // eslint-disable-next-line
    +beschaeftigungsgrad > 0
  ) {
    const rangeValue = [new RangeValues(0, 3.99999), new RangeValues(100, 150)];
    validator[MitarbeiterProperties.anMonatslohnBeschaeftigungsgrad] =
      extendedBetweenValidator(beschaeftigungsgrad, rangeValue, true);
  } else if (
    validator[
      MitarbeiterProperties.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
    ] === null &&
    // eslint-disable-next-line
    +bezahlteStundenImReferenzmonat > 0
  ) {
    const rangeValue = new RangeValues(220, 300);
    validator[
      MitarbeiterProperties.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
    ] = extendedBetweenValidator(
      bezahlteStundenImReferenzmonat,
      rangeValue,
      true
    );
  }
}

function shouldSetValidator(
  bezahlteStundenImReferenzmonat,
  beschaeftigungsgrad
): boolean {
  return (
    (+bezahlteStundenImReferenzmonat > 0 && +beschaeftigungsgrad > 0) ||
    (+bezahlteStundenImReferenzmonat > 0 && +beschaeftigungsgrad < 0) ||
    (+bezahlteStundenImReferenzmonat < 0 && +beschaeftigungsgrad > 0) ||
    (+bezahlteStundenImReferenzmonat < 0 && +beschaeftigungsgrad < 0) ||
    (+bezahlteStundenImReferenzmonat < 0 && +beschaeftigungsgrad === 0) ||
    (+bezahlteStundenImReferenzmonat === 0 && +beschaeftigungsgrad < 0) ||
    (+bezahlteStundenImReferenzmonat === 0 && +beschaeftigungsgrad === 0) ||
    (isRequiredValue(bezahlteStundenImReferenzmonat) &&
      isRequiredValue(beschaeftigungsgrad))
  );
}

function setValidatorForBeschaetigunsgradAndBezahlStunden(
  validator: TemostaDataListValidationDto[]
) {
  validator[MitarbeiterProperties.anMonatslohnBeschaeftigungsgrad] =
    getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.TOGGLE_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      true
    );
  validator[
    MitarbeiterProperties.anStundenlohnAnzahlBezahlteStundenImReferenzmonat
  ] = getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.TOGGLE_VALUE,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    true
  );
}

function countDecimals(value: number): number {
  // eslint-disable-next-line
  const toCheck = +value;
  if (!value || Math.floor(toCheck) === toCheck) {
    return 0;
  }
  const fragments = toCheck.toString().split('.') || ['', ''];
  if (fragments.length === 1) {
    return 0;
  }
  return fragments[1].length || 0;
}

function betweenWithOnlyOnePartAndContainsEqualOnLastTermeValidator(
  controlValue: string | number,
  min: number,
  max: number,
  requiredValue: boolean,
  rangeValues?: RangeValues
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }
  if ((+controlValue >= min && +controlValue <= max) || !requiredValue) {
    const isAuffaellige = extendedBetweencontainsOnlyLastEqualsValidator(
      controlValue,
      rangeValues
    );
    if (isAuffaellige) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUFFAELLIGE_RANGE_ERROR,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }

    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function betweenWithOnlyOnePartAndContainsEqualOnFirstTermeValidator(
  controlValue: string | number,
  min: number,
  max: number,
  requiredValue: boolean,
  rangeValues?: RangeValues
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }
  if ((+controlValue >= min && +controlValue <= max) || !requiredValue) {
    const isAuffaellige = extendedBetweencontainsBothEqualsValidator(
      controlValue,
      rangeValues
    );
    if (isAuffaellige) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUFFAELLIGE_RANGE_ERROR,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }

    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function betweenWithBothTermeWithEqualsValidator(
  controlValue: string | number,
  min: number,
  max: number,
  requiredValue: boolean,
  rangeValues?: RangeValues
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }
  if ((+controlValue >= min && +controlValue <= max) || !requiredValue) {
    const isAuffaellige = extendedBetweencontainsOnlyFirstEqualsValidator(
      controlValue,
      rangeValues
    );
    if (isAuffaellige) {
      return getTesmostaDataValidator(
        VALIDATOR_ERROR_NAMES.AUFFAELLIGE_RANGE_ERROR,
        TesmostaDatalistValidatorValue.AuffaelligerWert,
        false
      );
    }

    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function extendedBetweenValidator(
  controlValue: string | number,
  rangeValues: RangeValues | RangeValues[],
  onlyGreaterForFirstTerme: boolean
): TemostaDataListValidationDto {
  let isAuffaellige: boolean;
  if (onlyGreaterForFirstTerme) {
    isAuffaellige = extendedBetweencontainsOnlyLastEqualsValidator(
      controlValue,
      rangeValues
    );
  } else {
    isAuffaellige = extendedBetweencontainsOnlyFirstEqualsValidator(
      controlValue,
      rangeValues
    );
  }

  if (isAuffaellige) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.AUFFAELLIGE_RANGE_ERROR,
      TesmostaDatalistValidatorValue.AuffaelligerWert,
      false
    );
  }

  return null;
}

function betweenValidator(
  controlValue: string | number,
  min: number,
  max: number,
  requiredValue: boolean
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }

  if (hasValue(controlValue) && +controlValue > 0) {
    if (+controlValue >= min && +controlValue <= max) {
      return null;
    }
  } else if (!requiredValue) {
    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function integerBetweenValidator(
  controlValue: string | number,
  min: number,
  max: number,
  requiredValue: boolean
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue)) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }

  const value = +controlValue;
  if (Number.isInteger(value) && value >= min && value <= max) {
    return null;
  }
  if (hasValue(controlValue) && value > 0) {
    if (Number.isInteger(value) && value >= min && value <= max) {
      return null;
    }
  } else if (!requiredValue) {
    return null;
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function isAllowValidation(grundgesamtheit: string | number) {
  if (grundgesamtheit && +grundgesamtheit === 1) {
    return true;
  }
  return false;
}

function getTesmostaDataValidator(
  regleName: string,
  value: TesmostaDatalistValidatorValue,
  required: boolean
): TemostaDataListValidationDto {
  return {regleName, value, required: required};
}

function alphanumerischValidator(
  controlValue: string,
  requiredValue: boolean,
  allowAccent
): TemostaDataListValidationDto {
  const regex: RegExp = allowAccent
    ? new RegExp(
        /^[a-zA-Z0-9ÁÀÂÃÇÉÈÊÍÌÎÏÑÓÒÔÕÚÙÛÝÅËß»áàâãçéèêíìîïñóòôõúùûýåæðë│«¦‗÷¶¸¨;.·:_\-#+*!"§$%&()=?°<>::\-/¨.@öäàüÜéè,.\\]*$/gm
      )
    : new RegExp(/^[a-zA-Z0-9]*$/gm);

  if (
    (controlValue &&
      controlValue.length > 0 &&
      // eslint-disable-next-line
      String(controlValue).match(regex)) ||
    !requiredValue
  ) {
    return null;
  }

  if (!controlValue) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }
  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.ONLY_ALPHABETS,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function hasValue(value: string | number): boolean {
  if (value === 0 || value === '0' || value !== null || value === undefined) {
    return true;
  }
  return false;
}

function isRequiredValue(value: string | number): boolean {
  if (value === '' || value === null || value === undefined) {
    return true;
  }
  return false;
}

function isRequiredNumberValue(value: string | number): boolean {
  if (isRequiredValue(value) || isNaN(+value)) {
    return true;
  }
  return false;
}

function isEmpty(value) {
  if (value) {
    return value.toString()?.trim().length === 0;
  }
  // eslint-disable-next-line
  return !!(value === undefined || value == null);
}

function minValidator(
  requiredValue: boolean,
  controlValue: string | number,
  allowedZero: boolean
): TemostaDataListValidationDto {
  if (isRequiredNumberValue(controlValue) && requiredValue) {
    return getTesmostaDataValidator(
      VALIDATOR_ERROR_NAMES.REQUIRED_VALUE,
      TesmostaDatalistValidatorValue.UngueltigerWert,
      requiredValue
    );
  }

  if (allowedZero && +controlValue >= 0) {
    return null;
  }
  if (+controlValue > 0) {
    return null;
  }

  return getTesmostaDataValidator(
    VALIDATOR_ERROR_NAMES.RANGE_ERROR,
    TesmostaDatalistValidatorValue.UngueltigerWert,
    requiredValue
  );
}

function extendedBetweencontainsBothEqualsValidator(
  controlValue: string | number,
  rangeValues: RangeValues
): boolean {
  if (!rangeValues) {
    return false;
  }

  const isValidFirstPart =
    +controlValue >= rangeValues.min1 && +controlValue <= rangeValues.max1;
  if (isValidFirstPart) {
    return true;
  }
  if (rangeValues.min2 && rangeValues.max2) {
    return (
      +controlValue >= rangeValues.min2 && +controlValue <= rangeValues.max2
    );
  }

  return false;
}

function extendedBetweencontainsOnlyFirstEqualsValidator(
  controlValue: string | number,
  rangeValues: RangeValues | RangeValues[]
): boolean {
  if (!rangeValues) {
    return false;
  }

  const controlValueNum = +controlValue;

  const ranges =
    rangeValues instanceof Array
      ? rangeValues
      : ([rangeValues] as RangeValues[]);

  for (const range of ranges) {
    if (controlValueNum >= range.min1 && controlValueNum < range.max1) {
      return true;
    }
    if (range.min2 && range.max2) {
      return controlValueNum >= range.min2 && controlValueNum <= range.max2;
    }
  }
  return false;
}

function extendedBetweencontainsOnlyLastEqualsValidator(
  controlValue: string | number,
  rangeValues: RangeValues | RangeValues[]
): boolean {
  if (!rangeValues) {
    return false;
  }

  const ranges =
    rangeValues instanceof Array
      ? rangeValues
      : ([rangeValues] as RangeValues[]);

  for (const range of ranges) {
    const isValidFirstPart =
      +controlValue > range.min1 && +controlValue <= range.max1;
    if (isValidFirstPart) {
      return true;
    }
    if (range.min2 && range.max2) {
      return +controlValue >= range.min2 && +controlValue <= range.max2;
    }
  }

  return false;
}

function getAusbildungBksItemsWithInconsistentCodification(
  data: Mitarbeiter[]
): Mitarbeiter[] {
  let lsfOfFunctionWithDiffCodification = [] as Mitarbeiter[];
  let tmp = [];

  const grouped = getMitarbeiterGroupedByFunction(
    // eslint-disable-next-line
    data.filter(x => +x.grundgesamtheit === 1)
  );

  for (const prop of Object.keys(grouped)) {
    if (containsAusbildungBknDifferentCodification(grouped[prop])) {
      tmp = lsfOfFunctionWithDiffCodification;
      lsfOfFunctionWithDiffCodification = [...tmp, ...grouped[prop]];
    }
  }

  return lsfOfFunctionWithDiffCodification;
}

function getItemsWithInconsistentCodification(
  data: Mitarbeiter[]
): Mitarbeiter[] {
  let lsfOfFunctionWithDiffCodification = [] as Mitarbeiter[];
  let tmp = [];

  const grouped = getMitarbeiterGroupedByFunction(
    // eslint-disable-next-line
    data.filter(x => +x.grundgesamtheit === 1)
  );

  for (const prop of Object.keys(grouped)) {
    if (containsDifferentCodification(grouped[prop])) {
      tmp = lsfOfFunctionWithDiffCodification;
      lsfOfFunctionWithDiffCodification = [...tmp, ...grouped[prop]];
    }
  }

  return lsfOfFunctionWithDiffCodification;
}

function containsFunctionOrBstOrBknError(mitarbeiter: Mitarbeiter) {
  if (
    mitarbeiter.validator[MitarbeiterProperties.funktion]?.value ===
      TesmostaDatalistValidatorValue.UngueltigerWert ||
    mitarbeiter.validator[MitarbeiterProperties.betrieblichesKompetenzniveau]
      ?.value === TesmostaDatalistValidatorValue.UngueltigerWert ||
    mitarbeiter.validator[MitarbeiterProperties.beruflicheStellung]?.value ===
      TesmostaDatalistValidatorValue.UngueltigerWert ||
    isEmpty(mitarbeiter.funktion) ||
    isEmpty(mitarbeiter.beruflicheStellung) ||
    isEmpty(mitarbeiter.betrieblichesKompetenzniveau)
  ) {
    return true;
  }
  return false;
}

function containsAusbildungBknOrFunctionError(mitarbeiter: Mitarbeiter) {
  if (
    mitarbeiter.validator[MitarbeiterProperties.funktion]?.value ===
      TesmostaDatalistValidatorValue.UngueltigerWert ||
    mitarbeiter.validator[MitarbeiterProperties.betrieblichesKompetenzniveau]
      ?.value === TesmostaDatalistValidatorValue.UngueltigerWert ||
    mitarbeiter.validator[MitarbeiterProperties.ausbildung]?.value ===
      TesmostaDatalistValidatorValue.UngueltigerWert ||
    isEmpty(mitarbeiter.funktion) ||
    isEmpty(mitarbeiter.ausbildung) ||
    isEmpty(mitarbeiter.beruflicheStellung) ||
    isEmpty(mitarbeiter.betrieblichesKompetenzniveau)
  ) {
    return true;
  }
  return false;
}

function containsAusbildungBknDifferentCodification(
  mitarbeiters: Mitarbeiter[]
): boolean {
  const lstOfMitarbeiters = mitarbeiters ? mitarbeiters : [];

  if (lstOfMitarbeiters.length === 0 || lstOfMitarbeiters.length === 1) {
    return false;
  }

  const m = mitarbeiters.filter(x => !containsAusbildungBknOrFunctionError(x));

  const {betrieblichesKompetenzniveau} = lstOfMitarbeiters[0];
  const {beruflicheStellung} = lstOfMitarbeiters[0];

  const count = m.filter(
    x =>
      +x.betrieblichesKompetenzniveau === +betrieblichesKompetenzniveau &&
      +x.beruflicheStellung === +beruflicheStellung
  ).length;
  return count !== m.length;
}

function containsDifferentCodification(mitarbeiters: Mitarbeiter[]): boolean {
  const lstOfMitarbeiters = mitarbeiters ? mitarbeiters : [];

  if (lstOfMitarbeiters.length === 0 || lstOfMitarbeiters.length === 1) {
    return false;
  }

  const m = mitarbeiters.filter(x => !containsFunctionOrBstOrBknError(x));

  const {betrieblichesKompetenzniveau} = lstOfMitarbeiters[0];
  const {beruflicheStellung} = lstOfMitarbeiters[0];

  const count = m.filter(
    x =>
      +x.betrieblichesKompetenzniveau === +betrieblichesKompetenzniveau &&
      +x.beruflicheStellung === +beruflicheStellung
  ).length;
  return count !== m.length;
}

function getMitarbeiterGroupedByFunction(data: Mitarbeiter[]) {
  return data.reduce((previouMitarbeiter, crtMitarbeiter) => {
    previouMitarbeiter[crtMitarbeiter.funktion] =
      previouMitarbeiter[crtMitarbeiter.funktion] || [];
    previouMitarbeiter[crtMitarbeiter.funktion].push(crtMitarbeiter);
    return previouMitarbeiter;
  }, {});
}

class RangeValues {
  min1: number;
  max1: number;
  min2?: number;
  max2?: number;

  constructor(min1: number, max1: number, min2?: number, max2?: number) {
    this.min1 = min1;
    this.max1 = max1;
    this.min2 = min2;
    this.max2 = max2;
  }
}
